import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  X,
  Plus,
  Trash2,
  AlertCircle,
  Image as ImageIcon,
} from "lucide-react";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import Swal from "sweetalert2";

const UpdateBlogModal = ({ onClose, onSuccess, blog }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subDescriptions: [""],
    quote: { title: "", text: "" },
    feature: {
      heading: "",
      description: "",
      image: null,
      subDescriptions: [""],
      features: [""],
    },
    label: [{ text: "", image: null }],
  });

  const [mainFile, setMainFile] = useState(null);
  const [featureFile, setFeatureFile] = useState(null);
  const [labelPreviews, setLabelPreviews] = useState([]);
  const [images, setImages] = useState({ mainImage: null, featureImage: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prefill form when blog data is passed
  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || "",
        description: blog.description || "",
        subDescriptions: blog.subDescriptions?.length
          ? blog.subDescriptions
          : [""],
        quote: blog.quote || { title: "", text: "" },
        feature: blog.feature || {
          heading: "",
          description: "",
          image: null,
          subDescriptions: [""],
          features: [""],
        },
        label: blog.label?.length
          ? blog.label.map((l) => ({ text: l.text, image: l.image }))
          : [{ text: "", image: null }],
      });

      setImages({
        mainImage: blog.image || null,
        featureImage: blog.feature?.image || null,
      });

      setLabelPreviews(blog.label?.map((l) => l.image) || []);
    }
  }, [blog]);

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubDescChange = (index, value) => {
    const updated = [...formData.subDescriptions];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, subDescriptions: updated }));
  };

  const addSubDesc = () =>
    setFormData((prev) => ({
      ...prev,
      subDescriptions: [...prev.subDescriptions, ""],
    }));

  const removeSubDesc = (index) => {
    const updated = formData.subDescriptions.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      subDescriptions: updated.length ? updated : [""],
    }));
  };

  const handleQuoteChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      quote: { ...prev.quote, [field]: value },
    }));
  };

  const handleFeatureChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, [field]: value },
    }));
  };

  const handleFeatureSubDescChange = (index, value) => {
    const updated = [...formData.feature.subDescriptions];
    updated[index] = value;
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, subDescriptions: updated },
    }));
  };

  const addFeatureSubDesc = () =>
    setFormData((prev) => ({
      ...prev,
      feature: {
        ...prev.feature,
        subDescriptions: [...prev.feature.subDescriptions, ""],
      },
    }));

  const removeFeatureSubDesc = (index) => {
    const updated = formData.feature.subDescriptions.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      feature: {
        ...prev.feature,
        subDescriptions: updated.length ? updated : [""],
      },
    }));
  };

  const handleFeatureListChange = (index, value) => {
    const updated = [...formData.feature.features];
    updated[index] = value;
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, features: updated },
    }));
  };

  const addFeatureList = () =>
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, features: [...prev.feature.features, ""] },
    }));

  const removeFeatureList = (index) => {
    const updated = formData.feature.features.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, features: updated.length ? updated : [""] },
    }));
  };

  // Label management
  const handleLabelTextChange = (index, value) => {
    const updated = [...formData.label];
    updated[index].text = value;
    setFormData((prev) => ({ ...prev, label: updated }));
  };

  const handleLabelImage = (index, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setLabelPreviews((prev) => {
        const updated = [...prev];
        updated[index] = reader.result;
        return updated;
      });
    };
    reader.readAsDataURL(file);

    const updated = [...formData.label];
    updated[index].image = file;
    setFormData((prev) => ({ ...prev, label: updated }));
  };

  const addLabel = () =>
    setFormData((prev) => ({
      ...prev,
      label: [...prev.label, { text: "", image: null }],
    }));

  const removeLabel = (index) => {
    const updated = formData.label.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      label: updated.length ? updated : [{ text: "", image: null }],
    }));
    setLabelPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Image handling
  const handleMainImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMainFile(file);
    const reader = new FileReader();
    reader.onloadend = () =>
      setImages((prev) => ({ ...prev, mainImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleFeatureImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFeatureFile(file);
    const reader = new FileReader();
    reader.onloadend = () =>
      setImages((prev) => ({ ...prev, featureImage: reader.result }));
    reader.readAsDataURL(file);
  };

  // Validation
  const validate = () => {
    if (!formData.title.trim()) return "Blog title is required";
    if (!formData.description.trim()) return "Blog description is required";
    if (!formData.quote.title.trim() || !formData.quote.text.trim())
      return "Quote section is required";
    if (!formData.feature.heading.trim() || !formData.feature.description.trim())
      return "Feature section details required";
    if (!formData.label.length || formData.label.some((l) => !l.text.trim()))
      return "All labels must have text";
    return null;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) return setError(validationError);

    setError(null);
    setLoading(true);

    try {
      let mainImageUrl = blog.image;
      let featureImageUrl = blog.feature?.image;

      if (mainFile) mainImageUrl = await uploadToCloudinary(mainFile);
      if (featureFile) featureImageUrl = await uploadToCloudinary(featureFile);

      // Upload label images
      const uploadedLabels = await Promise.all(
        formData.label.map(async (l) => {
          if (typeof l.image === "string") return l; // already uploaded
          const imageUrl = l.image ? await uploadToCloudinary(l.image) : null;
          return { text: l.text, image: imageUrl };
        })
      );

      const submitData = {
        title: formData.title,
        description: formData.description,
        subDescriptions: formData.subDescriptions.filter((item) => item.trim() !== ""),
        image: mainImageUrl,
        quote: formData.quote,
        feature: {
          ...formData.feature,
          image: featureImageUrl,
          subDescriptions: formData.feature.subDescriptions.filter((item) => item.trim() !== ""),
          features: formData.feature.features.filter((item) => item.trim() !== ""),
        },
        label: uploadedLabels.filter((l) => l.text.trim() || l.image),
      };

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/blog/update-blog/${blog._id}`,
        submitData
      );

      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Blog updated successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to update blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white p-6 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-lg font-semibold text-white">Update Blog</h2>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5" /> {error}
            </div>
          )}

          <div>
            <label className="block font-semibold text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">Description *</label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
          </div>

          <div className="border rounded-xl p-4 space-y-3">
            <h4 className="font-semibold text-gray-800">Sub Descriptions *</h4>
            {formData.subDescriptions.map((desc, i) => (
              <div key={i} className="flex items-center gap-3">
                <input
                  type="text"
                  value={desc}
                  onChange={(e) => handleSubDescChange(i, e.target.value)}
                  placeholder={`Sub Description ${i + 1}`}
                  className="flex-1 border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                />
                <button type="button" onClick={() => removeSubDesc(i)} className="text-red-500">
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addSubDesc}
              className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
            >
              <Plus size={18} className="mr-1" /> Add Sub Description
            </button>
          </div>

          <div className="border rounded-xl p-4 space-y-3">
            <h4 className="font-semibold text-gray-800">Quote *</h4>
            <input
              type="text"
              placeholder="Quote Title"
              value={formData.quote.title}
              onChange={(e) => handleQuoteChange("title", e.target.value)}
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
            <textarea
              rows={3}
              placeholder="Quote Text"
              value={formData.quote.text}
              onChange={(e) => handleQuoteChange("text", e.target.value)}
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
          </div>

          <div className="border rounded-xl p-4 space-y-4">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Feature Heading *</label>
              <input
                type="text"
                placeholder="Feature Heading"
                value={formData.feature.heading}
                onChange={(e) => handleFeatureChange("heading", e.target.value)}
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Feature Description *</label>
              <textarea
                rows={3}
                placeholder="Feature Description"
                value={formData.feature.description}
                onChange={(e) => handleFeatureChange("description", e.target.value)}
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
              />
            </div>

            <div className="space-y-2">
              <h5 className="block font-semibold text-gray-700 mb-1">Feature Sub Descriptions *</h5>
              {formData.feature.subDescriptions.map((desc, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={desc}
                    onChange={(e) => handleFeatureSubDescChange(i, e.target.value)}
                    placeholder={`Feature Sub Description ${i + 1}`}
                    className="flex-1 border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeatureSubDesc(i)}
                    className="text-red-500"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeatureSubDesc}
                className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
              >
                <Plus size={18} className="mr-1" /> Add Feature Sub Description
              </button>
            </div>

            <div className="space-y-2">
              <h5 className="block font-semibold text-gray-700 mb-1">Feature List Items *</h5>
              {formData.feature.features.map((feat, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={feat}
                    onChange={(e) => handleFeatureListChange(i, e.target.value)}
                    placeholder={`Feature ${i + 1}`}
                    className="flex-1 border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeatureList(i)}
                    className="text-red-500"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeatureList}
                className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
              >
                <Plus size={18} className="mr-1" /> Add Feature Item
              </button>
            </div>
          </div>

          <div className="border rounded-xl p-4 space-y-3">
            <h4 className="block font-semibold text-gray-700 mb-1">Labels *</h4>
            {formData.label.map((label, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row gap-3 items-start md:items-center border p-3 rounded-lg"
              >
                <input
                  type="text"
                  value={label.text}
                  onChange={(e) => handleLabelTextChange(i, e.target.value)}
                  placeholder={`Label ${i + 1}`}
                  className="flex-1 border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                />
                <div className="flex flex-col items-center">
                  <label className="cursor-pointer flex items-center gap-2 text-[#0BB6D9]">
                    <ImageIcon size={18} /> Upload
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLabelImage(i, e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                  {labelPreviews[i] && (
                    <img
                      src={labelPreviews[i]}
                      alt="preview"
                      className="w-16 h-16 rounded mt-2 object-cover"
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeLabel(i)}
                  className="text-red-500 mt-2 md:mt-0"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addLabel}
              className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
            >
              <Plus size={18} className="mr-1" /> Add Label
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Main Image *
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleMainImage}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
              />
              {images.mainImage && (
                <img
                  src={images.mainImage}
                  alt="main"
                  className="mt-2 w-full h-40 object-cover rounded-lg"
                />
              )}
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Feature Image *
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFeatureImage}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
              />
              {images.featureImage && (
                <img
                  src={images.featureImage}
                  alt="feature"
                  className="mt-2 w-full h-40 object-cover rounded-lg"
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-lg bg-[#0BB6D9] text-white hover:bg-cyan-700 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="animate-spin w-4 h-4" />}
              <span>Update</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateBlogModal;